import { createHttpClient } from '@adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShippingRulePresetsApiAdapter } from '../adapters/ShippingRulePresetsApiAdapter';
import { CreateUpdatePresetPayload, ShippingRulePreset } from '../models/ShippingRulePreset';
import { normalizePresetRulesForApi } from '../utils/presetRuleDraft';

// Hook que centraliza todas las acciones sobre presets: crear, editar, eliminar, duplicar y aplicar
export const usePresetActions = (onSuccess?: () => void) => {
  const { t } = useTranslation('shippingRulePresetsModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const httpClient = createHttpClient(apiClientWH);
  const adapter = new ShippingRulePresetsApiAdapter(httpClient);

  // Extrae el mensaje de error de las distintas formas de respuesta de API
  const getApiErrorMessage = (error: any): string => {
    return (
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      error?.message ||
      t('toasts.error.unexpected')
    );
  };

  // Invalida caché de listados y detalle; espera refetch antes del callback (p. ej. navegar)
  const invalidateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['shipping-rule-presets'] }),
      queryClient.invalidateQueries({ queryKey: ['shipping-rule-preset-applications'] }),
      queryClient.invalidateQueries({ queryKey: ['shipping-rule-preset-one'] }),
    ]);
    onSuccess?.();
  };

  const createPreset = async (payload: CreateUpdatePresetPayload) => {
    setIsLoading(true);
    try {
      console.log('🔍 usePresetActions - createPreset:', payload);
      const created = await adapter.createPreset(payload);
      queryClient.setQueryData(['shipping-rule-preset-one', created.id], created);
      toast({
        title: t('toasts.create.success.title'),
        description: t('toasts.create.success.description'),
        variant: 'default',
      });
      await invalidateQueries();
    } catch (error: any) {
      console.log('🔍 usePresetActions - createPreset error:', {
        message: error.message,
        response: error.response,
      });
      toast({
        title: t('toasts.create.error.title'),
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreset = async (id: number, payload: Partial<CreateUpdatePresetPayload>) => {
    setIsLoading(true);
    try {
      console.log('🔍 usePresetActions - updatePreset:', { id, payload });
      const updated = await adapter.updatePreset(id, payload);
      queryClient.setQueryData(['shipping-rule-preset-one', id], updated);
      toast({
        title: t('toasts.update.success.title'),
        description: t('toasts.update.success.description'),
        variant: 'default',
      });
      await invalidateQueries();
    } catch (error: any) {
      console.log('🔍 usePresetActions - updatePreset error:', {
        message: error.message,
        response: error.response,
      });
      toast({
        title: t('toasts.update.error.title'),
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deletePreset = async (id: number) => {
    setIsLoading(true);
    try {
      console.log('🔍 usePresetActions - deletePreset:', { id });
      await adapter.deletePreset(id);
      queryClient.removeQueries({ queryKey: ['shipping-rule-preset-one', id] });
      toast({
        title: t('toasts.delete.success.title'),
        description: t('toasts.delete.success.description'),
        variant: 'default',
      });
      await invalidateQueries();
    } catch (error: any) {
      console.log('🔍 usePresetActions - deletePreset error:', {
        message: error.message,
        response: error.response,
      });
      toast({
        title: t('toasts.delete.error.title'),
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const duplicatePreset = async (preset: ShippingRulePreset) => {
    setIsLoading(true);
    try {
      console.log('🔍 usePresetActions - duplicatePreset:', { id: preset.id });
      const { rules, error } = normalizePresetRulesForApi(preset.rules_payload);
      if (error) {
        toast({
          title: t('toasts.duplicate.error.title'),
          description: error,
          variant: 'destructive',
        });
        return;
      }
      await adapter.createPreset({
        name: `${preset.name} (${t('duplicate.suffix')})`,
        description: preset.description,
        rules,
      });
      toast({
        title: t('toasts.duplicate.success.title'),
        description: t('toasts.duplicate.success.description'),
        variant: 'default',
      });
      await invalidateQueries();
    } catch (error: any) {
      console.log('🔍 usePresetActions - duplicatePreset error:', {
        message: error.message,
        response: error.response,
      });
      toast({
        title: t('toasts.duplicate.error.title'),
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyPreset = async (presetId: number, targetTeamId: number) => {
    setIsLoading(true);
    try {
      console.log('🔍 usePresetActions - applyPreset:', { presetId, targetTeamId });
      const result = await adapter.applyPreset(presetId, targetTeamId);
      toast({
        title: t('toasts.apply.success.title'),
        description: t('toasts.apply.success.description', {
          applied: result.rules_count,
          replaced: result.replaced_rules_count,
        }),
        variant: 'default',
      });
      await invalidateQueries();
      return result;
    } catch (error: any) {
      console.log('🔍 usePresetActions - applyPreset error:', {
        message: error.message,
        response: error.response,
      });
      toast({
        title: t('toasts.apply.error.title'),
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createPreset,
    updatePreset,
    deletePreset,
    duplicatePreset,
    applyPreset,
  };
};
